import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../utils/toast';
import { Folder } from '../icons/s2';
import { ActionButton } from "@react-spectrum/s2";
import { Settings, UserGroup, Key, ChevronLeft } from '../icons/s2';
import { BackButton } from '../common';
import { useResizablePanel } from '../../hooks/useResizablePanel';
import { useIsMobile } from '../../hooks/useIsMobile';
import { CCSite } from '../../types';
import { fetchCCOrgSites } from '../../services/api';
import type { OrgDetailSection } from '../../contexts/SiteContext';
import '../ChatBox/AppSidebar.css';

const DOMAINS_PANEL_PAGE_SIZE = 100;

export interface OrgDetailSidebarProps {
  orgId: string;
  orgName: string;
  activeSection: OrgDetailSection | null;
  domainsPanelOpen: boolean;
  selectedDomainSiteId: string | null;
  onBack: () => void;
  onToggleDomains: () => void;
  onSection: (section: OrgDetailSection) => void;
  onSelectDomain: (siteId: string) => void;
  /** True while the mobile off-canvas drawer holding this sidebar is open —
   *  used to reset the one-level "Back" state on every fresh open, mirroring
   *  AppSidebar's `isDrawerOpen` prop. */
  isDrawerOpen?: boolean;
  /** Closes the mobile off-canvas drawer after a terminal nav choice. */
  onMobileNavClose?: () => void;
}

/**
 * Primary-sidebar replacement while inside an org's detail view — matches
 * design-frames/org-detail.html's app-sidebar-primary (back arrow + org name
 * + Domains/Org Configuration/Model Allowlist nav rows) plus its toggleable
 * workspaces-sidebar domain list, both of which live in the app's primary
 * sidebar rail, NOT inside the page's own content area.
 */
export const OrgDetailSidebar: React.FC<OrgDetailSidebarProps> = ({
  orgId,
  orgName,
  activeSection,
  domainsPanelOpen,
  selectedDomainSiteId,
  onBack,
  onToggleDomains,
  onSection,
  onSelectDomain,
  isDrawerOpen = false,
  onMobileNavClose,
}) => {
  const { width: sidebarWidth, isDragging, onResizeStart: handleResizeStart } = useResizablePanel({
    minWidth: 200, maxWidth: 420, defaultWidth: 220, direction: 'right',
  });
  const [domains, setDomains] = useState<CCSite[]>([]);
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  // ---- One-level mobile nav (mirrors AppSidebar) ----
  // The Domains list is this sidebar's only secondary panel. On the mobile
  // drawer it and the primary rail must never show side by side (both get
  // squeezed unusably narrow), so exactly one column shows at a time, with a
  // Back button on the Domains panel to return to the rail.
  const [mobileSecondaryDismissed, setMobileSecondaryDismissed] = useState(false);
  const showMobileSecondary = useCallback(() => setMobileSecondaryDismissed(false), []);

  // Section rows navigate straight to a page, so they must close the mobile
  // drawer themselves (App's "close on section change" is a no-op when the
  // tapped section is already active — i.e. re-tapping the active row).
  const closeDrawerOnMobile = useCallback(() => {
    if (isMobile) onMobileNavClose?.();
  }, [isMobile, onMobileNavClose]);

  // Every time the drawer is freshly opened, default back to whichever level
  // the current state implies rather than remembering a stale "Back" tap.
  useEffect(() => {
    if (isDrawerOpen) setMobileSecondaryDismissed(false);
  }, [isDrawerOpen]);

  const mobileShowingSecondary = domainsPanelOpen && !mobileSecondaryDismissed;
  const sidebarAreaMobileClass = !domainsPanelOpen
    ? ''
    : mobileShowingSecondary
      ? ' sidebar-area--mobile-secondary'
      : ' sidebar-area--mobile-primary';

  useEffect(() => {
    let cancelled = false;
    fetchCCOrgSites(orgId, 1, DOMAINS_PANEL_PAGE_SIZE)
      .then((response) => {
        if (!cancelled) setDomains(response.sites);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        toast.negative(error instanceof Error ? error.message : t('commandCenter.orgDetailSidebar.failedToLoadDomains'));
      });
    return () => { cancelled = true; };
  }, [orgId, t]);

  return (
    <div className={`sidebar-area${sidebarAreaMobileClass}`} inert={isMobile && !isDrawerOpen}>
      <div className="sidebar-wrapper" style={isMobile ? undefined : { width: sidebarWidth }}>
        <div className="sidebar-container">
          <div className="sidebar-panel sidebar-panel--nav" style={{ transform: 'translateX(0)', opacity: 1 }}>
            <div className="workspace-header">
              <BackButton
                onPress={onBack}
                aria-label={t('commandCenter.orgDetailSidebar.backToOrganizations')}
              />
              <div className="workspace-title" title={orgName}>
                {orgName}
              </div>
            </div>

            <div className="actions-list">
              <button
                type="button"
                className={`action-row ${domainsPanelOpen ? 'action-row--active' : ''}`}
                onClick={() => {
                  onToggleDomains();
                  showMobileSecondary();
                }}
                aria-label={t('commandCenter.orgDetailSidebar.domains')}
              >
                <span className="action-icon-wrap">
                  <Folder size="S" />
                </span>
                {t('commandCenter.orgDetailSidebar.domains')}
              </button>

              <button
                type="button"
                className={`action-row ${activeSection === 'orgconfig' ? 'action-row--active' : ''}`}
                onClick={() => { onSection('orgconfig'); closeDrawerOnMobile(); }}
                aria-label={t('commandCenter.orgDetailSidebar.orgConfiguration')}
              >
                <span className="action-icon-wrap">
                  <Settings size="S" />
                </span>
                {t('commandCenter.orgDetailSidebar.orgConfig')}
              </button>

              <button
                type="button"
                className={`action-row ${activeSection === 'orgadmins' ? 'action-row--active' : ''}`}
                onClick={() => { onSection('orgadmins'); closeDrawerOnMobile(); }}
                aria-label={t('commandCenter.orgDetailSidebar.orgAdmins')}
              >
                <span className="action-icon-wrap">
                  <UserGroup size="S" />
                </span>
                {t('commandCenter.orgDetailSidebar.orgAdminsLabel')}
              </button>

              <button
                type="button"
                className={`action-row ${activeSection === 'allowlist' ? 'action-row--active' : ''}`}
                onClick={() => { onSection('allowlist'); closeDrawerOnMobile(); }}
                aria-label={t('commandCenter.orgDetailSidebar.models')}
              >
                <span className="action-icon-wrap">
                  <Key size="S" />
                </span>
                {t('commandCenter.orgDetailSidebar.models')}
              </button>
            </div>
          </div>
        </div>
        <div
          className={`resize-handle ${isDragging ? 'resize-handle--dragging' : ''}`}
          onMouseDown={handleResizeStart}
          role="separator"
          aria-orientation="vertical"
        />
      </div>

      {domainsPanelOpen && (
        <div className="sidebar-secondary-shell" style={isMobile ? undefined : { width: 260 }}>
          <div className="workspaces-sidebar">
            <div className="workspaces-sidebar-header">
              <div className="secondary-panel-heading">
                {isMobile && (
                  <ActionButton
                    isQuiet
                    onPress={() => setMobileSecondaryDismissed(true)}
                    aria-label={t('commandCenter.orgDetailSidebar.backToMenu')}
                    UNSAFE_className="secondary-panel-back-btn"
                  >
                    <ChevronLeft size="S" />
                  </ActionButton>
                )}
                <div className="workspaces-sidebar-title">
                  <Folder size="S" />
                  {t('commandCenter.orgDetailSidebar.domains')}
                </div>
              </div>
            </div>
            <div className="workspaces-sidebar-list">
              {domains.map((domain) => (
                <button
                  key={domain.site_id}
                  type="button"
                  className={`context-row ${selectedDomainSiteId === domain.site_id ? 'context-row--active' : ''}`}
                  onClick={() => { onSelectDomain(domain.site_id); closeDrawerOnMobile(); }}
                  aria-label={domain.site_name}
                >
                  <div className="context-name-wrap">
                    <span className="project-icon-wrap">
                      <Folder size="S" />
                    </span>
                    <span className="context-name">{domain.site_name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgDetailSidebar;
